import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Instagram, Facebook, Youtube, Star } from "lucide-react";
import { NAV_LINKS, DIVISION_LINKS } from "@/data/content";
import { useSettings } from "@/hooks/useSettings";

export default function Footer() {
  const s = useSettings();
  return (
    <footer
      data-testid="site-footer"
      className="relative grain overflow-hidden bg-navy-deep text-white"
    >
      <div className="relative z-10 mx-auto max-w-7xl px-5 lg:px-8 py-20">
        <div className="grid gap-12 md:grid-cols-12">
          <div className="md:col-span-4">
            <div className="flex items-center gap-3 mb-6">
              <span className="flex h-14 w-14 items-center justify-center rounded-sm bg-white/95 p-1">
                <img src="/uyi-logo.png" alt="United Youngs India logo" className="h-full w-full object-contain" />
              </span>
              <span className="font-display text-xl">United Youngs India</span>
            </div>
            <p className="text-white/60 leading-relaxed text-sm max-w-sm">
              {s.tagline}. A premier construction, interior and furniture house
              crafting spaces across Lucknow & Uttar Pradesh.
            </p>
            <div className="mt-6 flex items-center gap-2 text-gold">
              <Star className="h-4 w-4 fill-gold" />
              <span className="text-sm text-white/80">
                {s.rating} Google Rating · {s.years_experience} years
              </span>
            </div>
            <div className="mt-6 flex gap-3">
              {s.instagram && (
                <a href={s.instagram} target="_blank" rel="noreferrer" data-testid="footer-instagram" className="flex h-10 w-10 items-center justify-center rounded-sm border border-white/15 hover:bg-gold hover:text-navy transition-colors duration-300">
                  <Instagram className="h-4 w-4" />
                </a>
              )}
              {s.facebook && (
                <a href={s.facebook} target="_blank" rel="noreferrer" data-testid="footer-facebook" className="flex h-10 w-10 items-center justify-center rounded-sm border border-white/15 hover:bg-gold hover:text-navy transition-colors duration-300">
                  <Facebook className="h-4 w-4" />
                </a>
              )}
              {s.youtube && (
                <a href={s.youtube} target="_blank" rel="noreferrer" data-testid="footer-youtube" className="flex h-10 w-10 items-center justify-center rounded-sm border border-white/15 hover:bg-gold hover:text-navy transition-colors duration-300">
                  <Youtube className="h-4 w-4" />
                </a>
              )}
            </div>
          </div>

          <div className="md:col-span-2">
            <h4 className="text-xs uppercase tracking-[0.28em] text-gold mb-5">Explore</h4>
            <ul className="space-y-3 text-sm">
              {NAV_LINKS.map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="text-white/60 hover:text-white transition-colors">{l.label}</Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-2">
            <h4 className="text-xs uppercase tracking-[0.28em] text-gold mb-5">Divisions</h4>
            <ul className="space-y-3 text-sm">
              {DIVISION_LINKS.map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="text-white/60 hover:text-white transition-colors">{l.label}</Link>
                </li>
              ))}
            </ul>
            <h4 className="text-xs uppercase tracking-[0.28em] text-gold mt-7 mb-5">Legal</h4>
            <ul className="space-y-3 text-sm">
              <li><Link to="/legal/privacy" data-testid="footer-menu-privacy" className="text-white/60 hover:text-white transition-colors">Privacy Policy</Link></li>
              <li><Link to="/legal/terms" data-testid="footer-menu-terms" className="text-white/60 hover:text-white transition-colors">Terms &amp; Conditions</Link></li>
              <li><Link to="/legal/disclaimer" data-testid="footer-menu-disclaimer" className="text-white/60 hover:text-white transition-colors">Disclaimer</Link></li>
            </ul>
          </div>

          <div className="md:col-span-4">
            <h4 className="text-xs uppercase tracking-[0.28em] text-gold mb-5">Get in touch</h4>
            <ul className="space-y-4 text-sm text-white/70">
              <li className="flex gap-3"><MapPin className="h-5 w-5 text-gold shrink-0" /><span>{s.address}</span></li>
              <li className="flex gap-3"><Phone className="h-5 w-5 text-gold shrink-0" /><span>{s.phone_primary}<br />{s.phone_secondary}</span></li>
              <li className="flex gap-3"><Mail className="h-5 w-5 text-gold shrink-0" /><span>{s.email}</span></li>
            </ul>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-white/40">
          <span>© {new Date().getFullYear()} United Youngs India. All rights reserved.</span>
          <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2">
            <Link to="/legal/privacy" data-testid="footer-privacy" className="hover:text-gold transition-colors">Privacy Policy</Link>
            <span className="text-white/20">·</span>
            <Link to="/legal/terms" data-testid="footer-terms" className="hover:text-gold transition-colors">Terms &amp; Conditions</Link>
            <span className="text-white/20">·</span>
            <Link to="/legal/disclaimer" data-testid="footer-disclaimer" className="hover:text-gold transition-colors">Disclaimer</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
